import { FC, useEffect, useRef, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { ClickableWhiteWrapper, WhiteWrapper } from '../styles';
import { formatTime, parseTime } from '@/helpers/formatTime';
import { TranscriptList } from './styles';
import { Transcript as TranscriptType } from '@/types/note';
import useAudioStore from '@/stores/audioStore';
import useVideoStore from '@/stores/videoStore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { SaveOutlined } from '@mui/icons-material';
import { ref, uploadString } from 'firebase/storage';
import { storage } from '@/firebase/firebase';
import useAlertStore from '@/stores/alertStore';
import { getTranscriptDescription, prepareTranscriptEntriesForSave } from '@/utils/transcript';
import { errorReporter } from '@/services/errorReportingService';
import { useParams } from 'react-router';

interface TranscriptProps {
  transcriptList: TranscriptType[];
  /** Whether this is audio-only mode */
  isAudioOnly?: boolean;
  isOwner?: boolean;
  isProcessing?: boolean;
  fullTranscriptUrl?: string | null;
  onKeyPointsUpdate?: (keyPoints: TranscriptType[]) => void;
}

const FullTranscript: FC<TranscriptProps> = ({
  transcriptList,
  isAudioOnly,
  isOwner = false,
  isProcessing = false,
  fullTranscriptUrl,
  onKeyPointsUpdate,
}) => {
  const { noteId } = useParams();
  const showAlert = useAlertStore((state) => state.showAlert);
  const {
    setWavesurferCurrentTime,
    setProgressCurrentTime: setAudioProgressCurrentTime,
    setProgressTime: setAudioProgressTime,
  } = useAudioStore();
  const {
    setVideoCurrentTime,
    setProgressCurrentTime: setVideoProgressCurrentTime,
    setProgressTime: setVideoProgressTime,
  } = useVideoStore();
  const [editableTranscriptList, setEditableTranscriptList] = useState<TranscriptType[]>(transcriptList);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const showEditButton = isOwner && !isProcessing && Boolean(fullTranscriptUrl) && transcriptList.length > 0;
  const keyPointRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (!isEditing) {
      setEditableTranscriptList(transcriptList);
    }
  }, [transcriptList, isEditing]);

  const handleClick = (timeInSeconds: number) => {
    if (isAudioOnly) {
      setAudioProgressCurrentTime(timeInSeconds);
      setAudioProgressTime();
      setWavesurferCurrentTime();
    } else {
      setVideoCurrentTime(timeInSeconds);
      setVideoProgressCurrentTime(timeInSeconds);
      setVideoProgressTime();
    }
  };

  /** Read current text from the DOM contentEditable elements. */
  const getEditedKeyPoints = (): TranscriptType[] => {
    return editableTranscriptList.map((entry, index) => {
      const editedText = keyPointRefs.current[index]?.textContent;
      if (editedText == null) return entry;

      const originalDescription = getTranscriptDescription(entry);
      if (editedText === originalDescription) return entry;

      return { ...entry, title: null, description: editedText, text: editedText };
    });
  };

  const handleEditOrSave = async () => {
    if (!isEditing) {
      setEditableTranscriptList(transcriptList);
      setIsEditing(true);
      return;
    }

    const updatedKeyPoints = getEditedKeyPoints();

    if (!fullTranscriptUrl || JSON.stringify(updatedKeyPoints) === JSON.stringify(transcriptList)) {
      setIsEditing(false);
      return;
    }

    // `ref(storage, ...)` accepts gs:// and firebasestorage.googleapis.com
    // download URLs — both write fine. Signed Cloud CDN URLs (cdn.notego.ai)
    // are read-only; we need a gs:// path from the API for those. Bail cleanly
    // instead of throwing storage/invalid-url at the user.
    const canWriteToFirebaseStorage =
      fullTranscriptUrl.startsWith('gs://') || fullTranscriptUrl.includes('firebasestorage.googleapis.com');
    if (!canWriteToFirebaseStorage) {
      showAlert('Editing key points is temporarily unavailable. Please try again later.', 'error');
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      const preparedEntries = prepareTranscriptEntriesForSave(updatedKeyPoints);
      await uploadString(ref(storage, fullTranscriptUrl), JSON.stringify(preparedEntries, null, 2), 'raw', {
        contentType: 'application/json',
      });
      setEditableTranscriptList(preparedEntries);
      onKeyPointsUpdate?.(preparedEntries);
      setIsEditing(false);
      showAlert('Key points updated successfully.', 'success');
    } catch (error) {
      errorReporter.expected('Failed to update key points in Firebase Storage', error, {
        feature: 'notes',
        operation: 'saveKeyPoints',
        noteId,
      });
      showAlert('Something went wrong while updating the key points. Try again later.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditableTranscriptList(transcriptList);
    setIsEditing(false);
  };

  const renderTranscripts = () => (
    <>
      {editableTranscriptList?.map((transcript, index) => {
        const timeInSeconds = parseTime(transcript.timecode);
        const description = getTranscriptDescription(transcript);

        return (
          <ClickableWhiteWrapper
            key={`${transcript.timecode}-${index}`}
            onClick={isEditing ? undefined : () => handleClick(timeInSeconds)}
            sx={isEditing ? { cursor: 'text' } : undefined}
          >
            <Typography variant="body2" sx={{ minWidth: '42px', flexShrink: 0 }}>
              {formatTime(timeInSeconds)}
            </Typography>
            {isEditing ? (
              <Typography
                variant="body2"
                contentEditable={!isSaving}
                suppressContentEditableWarning
                ref={(el: HTMLElement | null) => {
                  keyPointRefs.current[index] = el;
                }}
                sx={{
                  outline: 'none',
                  flex: 1,
                  backgroundColor: 'action.hover',
                  borderRadius: '4px',
                  px: 0.5,
                }}
              >
                {description || ''}
              </Typography>
            ) : (
              <Typography variant="body2">{description || 'Untitled key point'}</Typography>
            )}
          </ClickableWhiteWrapper>
        );
      })}
    </>
  );

  return (
    <>
      {showEditButton && (
        <Stack direction="row" justifyContent="flex-end" mb={1}>
          <Stack direction="row" spacing={1}>
            {isEditing && (
              <Button variant="text" color="inherit" onClick={handleCancel} disabled={isSaving}>
                Cancel
              </Button>
            )}
            <Button
              variant="text"
              color="primary"
              startIcon={isEditing ? <SaveOutlined /> : <EditOutlinedIcon />}
              onClick={handleEditOrSave}
              disabled={isSaving}
            >
              {isEditing ? 'Save Changes' : 'Edit'}
            </Button>
          </Stack>
        </Stack>
      )}
      <TranscriptList>
        {transcriptList?.length > 0 ? (
          renderTranscripts()
        ) : (
          <WhiteWrapper mb="24px" height={60} alignItems="center" justifyContent="center">
            <Typography variant="body1">No key points available</Typography>
          </WhiteWrapper>
        )}
      </TranscriptList>
    </>
  );
};

export default FullTranscript;
